import { useEffect, useState, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { CheckCircle, SpinnerGap, WarningCircle } from "@phosphor-icons/react";
import { api } from "../lib/api";
import { useCart } from "../context/CartContext";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { clear } = useCart();
  const [status, setStatus] = useState("checking");
  const attempts = useRef(0);
  const cleared = useRef(false);

  useEffect(() => {
    const sessionId = params.get("session_id");
    if (!sessionId) {
      setStatus("error");
      return;
    }

    let timer;
    const poll = async () => {
      if (attempts.current >= 8) {
        setStatus("pending");
        return;
      }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (!cleared.current) {
            cleared.current = true;
            clear();
          }
          return;
        }
        if (data.status === "expired" || data.payment_status === "failed") {
          setStatus("error");
          return;
        }
      } catch {
        /* keep polling */
      }
      timer = setTimeout(poll, 2000);
    };
    poll();
    return () => clearTimeout(timer);
  }, [params, clear]);

  return (
    <main
      data-testid="payment-success-page"
      className="min-h-screen flex items-center justify-center bg-[#FAF9F6] px-6"
    >
      <div className="max-w-lg text-center bg-white border border-[#EBEBEB] rounded-[2rem] p-12">
        {status === "checking" && (
          <>
            <SpinnerGap size={48} className="animate-spin text-[#A3B18A] mx-auto" />
            <h1 className="font-display text-3xl text-[#2D3732] mt-6">Confirmando tu pago…</h1>
            <p className="text-[#6B705C] mt-3">Un momento, estamos verificando la transacción.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle size={56} weight="fill" className="text-[#8A9A6F] mx-auto" />
            <h1 className="font-display text-4xl text-[#2D3732] mt-6">¡Gracias por tu pedido!</h1>
            <p className="text-[#4A554E] mt-4 leading-relaxed font-light">
              Tu compra se ha completado con éxito. Muy pronto empezarás a disfrutar de la
              tranquilidad de tener toda tu salud organizada.
            </p>
            <button
              data-testid="success-home-btn"
              onClick={() => navigate("/")}
              className="mt-8 bg-[#A3B18A] hover:bg-[#8A9A6F] text-white rounded-full px-8 py-4 text-sm transition-colors"
            >
              Volver al inicio
            </button>
          </>
        )}
        {status === "pending" && (
          <>
            <SpinnerGap size={48} className="text-[#A3B18A] mx-auto" />
            <h1 className="font-display text-3xl text-[#2D3732] mt-6">Pago en proceso</h1>
            <p className="text-[#6B705C] mt-3">
              Tu pago se está procesando. Te enviaremos la confirmación por correo en breve.
            </p>
            <button onClick={() => navigate("/")} className="mt-8 text-[#8A9A6F] link-underline">
              Volver al inicio
            </button>
          </>
        )}
        {status === "error" && (
          <>
            <WarningCircle size={48} className="text-[#b05656] mx-auto" />
            <h1 className="font-display text-3xl text-[#2D3732] mt-6">Algo salió mal</h1>
            <p className="text-[#6B705C] mt-3">No pudimos confirmar el pago. Inténtalo de nuevo.</p>
            <button onClick={() => navigate("/")} className="mt-8 text-[#8A9A6F] link-underline">
              Volver al inicio
            </button>
          </>
        )}
      </div>
    </main>
  );
}
